<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
  onClose: {
    type: Function,
    required: true,
  },
});

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const pipelines = ref([]);
const selectedPipelineId = ref(null);
const loading = ref(false);
const submitting = ref(false);

const selectedPipeline = computed(() =>
  pipelines.value.find(
    pipeline => pipeline.id === Number(selectedPipelineId.value)
  )
);

const loadPipelines = async () => {
  loading.value = true;
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/sales_pipelines`
    );
    pipelines.value = (response.data.payload || []).filter(
      pipeline => pipeline.active && pipeline.stages.some(stage => stage.active)
    );
    selectedPipelineId.value =
      pipelines.value.find(pipeline => pipeline.is_default)?.id ||
      pipelines.value[0]?.id ||
      null;
  } catch (error) {
    useAlert('Não foi possível carregar os funis disponíveis.');
  } finally {
    loading.value = false;
  }
};

const createOpportunity = async () => {
  if (!selectedPipelineId.value) {
    useAlert(
      'Crie ou ative um funil com uma etapa ativa antes de adicionar o atendimento.'
    );
    return;
  }

  submitting.value = true;
  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/sales_opportunities/create_for_conversation`,
      {
        conversation_display_id: props.conversationId,
        sales_pipeline_id: selectedPipelineId.value,
      }
    );
    const opportunity = response.data.payload;

    await store.dispatch('getConversation', props.conversationId);
    store.commit('UPDATE_CONVERSATION', {
      id: props.conversationId,
      sales_opportunity: {
        id: opportunity.id,
        title: opportunity.title,
        sales_pipeline_id: opportunity.sales_pipeline_id,
        sales_pipeline_stage_id: opportunity.sales_pipeline_stage_id,
      },
    });

    useAlert(
      response.data.already_linked
        ? 'Esta conversa já está vinculada ao Pipeline.'
        : 'Atendimento adicionado ao Pipeline.'
    );
    props.onClose();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível adicionar este atendimento ao Pipeline.'
    );
  } finally {
    submitting.value = false;
  }
};

onMounted(loadPipelines);
</script>

<template>
  <div class="w-full max-w-lg p-6">
    <div class="flex items-start justify-between gap-4 mb-5">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('SALES_PIPELINE.ADD_TO_PIPELINE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('SALES_PIPELINE.ADD_DESCRIPTION') }}
        </p>
      </div>
    </div>

    <label
      class="block text-sm font-medium text-n-slate-12"
      for="sales-pipeline-select"
    >
      {{ $t('SALES_PIPELINE.FUNNEL') }}
    </label>
    <div class="relative mt-2">
      <select
        id="sales-pipeline-select"
        v-model="selectedPipelineId"
        :disabled="loading || submitting || !pipelines.length"
        class="h-12 w-full appearance-none rounded-lg border border-n-weak bg-n-alpha-2 py-2 pl-3 pr-10 text-n-slate-12 focus:border-n-brand focus:ring-2 focus:ring-n-brand disabled:opacity-60"
      >
        <option
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          :value="pipeline.id"
        >
          {{ pipeline.name
          }}{{
            pipeline.is_default ? ` (${$t('SALES_PIPELINE.DEFAULT')})` : ''
          }}
        </option>
      </select>
      <span
        class="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center"
        ><i class="i-lucide-chevron-down size-4 text-n-slate-11"
      /></span>
    </div>

    <p v-if="!loading && !pipelines.length" class="mt-3 text-sm text-n-ruby-11">
      {{ $t('SALES_PIPELINE.NO_ACTIVE_FUNNEL') }}
    </p>
    <p v-else-if="selectedPipeline" class="mt-3 text-sm text-n-slate-11">
      {{ $t('SALES_PIPELINE.OPPORTUNITY_STAGE') }}
      <strong class="text-n-slate-12">{{
        selectedPipeline.stages.find(stage => stage.active)?.name
      }}</strong
      >{{ $t('COMMON.SENTENCE_PERIOD') }}
    </p>

    <div class="flex justify-end gap-3 mt-7">
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
        :disabled="submitting"
        @click="onClose"
      >
        {{ $t('DIALOG.BUTTONS.CANCEL') }}
      </button>
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand/90 disabled:opacity-60"
        :disabled="loading || submitting || !selectedPipelineId"
        @click="createOpportunity"
      >
        {{
          submitting
            ? $t('SALES_PIPELINE.ADDING')
            : $t('SALES_PIPELINE.ADD_TO_PIPELINE')
        }}
      </button>
    </div>
  </div>
</template>
